import { useQuery } from '@tanstack/react-query';

import { CHAIN_IDS } from '@/constants/chain';

import { useFeature } from '@/hooks/useFeature';

import type { DedaubTokenSecurityResponse } from '@/utils/0x/dedaub/types';
import { DEDAUB_TOKEN_CHECK } from '@/utils/featureFlags';
import { fetcher } from '@/utils/fetcher';
import { isKnownToken } from '@/utils/multichain';
import { tokenKey } from '@/utils/normalizeAddress';
import { routes } from '@/utils/routes';

type Params = {
  address?: string;
  chainId?: number;
  enabled?: boolean;
};

export const dedaubTokenCheckQueryKey = (address?: string, chainId?: number) =>
  ['token-security', 'dedaub', address && chainId ? tokenKey(address, chainId) : ''] as const;

export const useDedaubTokenCheck = ({ address, chainId, enabled = true }: Params) => {
  const isFlagEnabled = useFeature(DEDAUB_TOKEN_CHECK);

  // Currently, only scan Robinhood tokens that are not known tokens.
  const isEligible =
    address != null && chainId === CHAIN_IDS.ROBINHOOD && !isKnownToken(address, chainId);

  const query = useQuery<DedaubTokenSecurityResponse, Error>({
    queryKey: dedaubTokenCheckQueryKey(address, chainId),
    queryFn: () =>
      fetcher(`${routes.api.TOKENS_DEDAUB_SECURITY}?chainId=${chainId}&address=${address}`),
    enabled: enabled && isEligible,
    staleTime: 60_000,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    data: query.data?.dedaubScan,
    isLoading: enabled && isEligible && query.isLoading,
    isError: query.isError,
    isFeatureEnabled: isFlagEnabled && isEligible,
  };
};
